package com.karyadina.ui.components

import androidx.compose.animation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Android
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.karyadina.service.AppStorage
import com.karyadina.util.isWeb
import com.karyadina.util.getCurrentTimeMs
import com.karyadina.util.openPlayStore

/**
 * Android promotion banner for web platform
 * Shows creative taglines and download button
 * Dismissible with 7-day localStorage persistence
 */
@Composable
fun AndroidPromotionBanner(
    modifier: Modifier = Modifier
) {
    // Only show on web
    if (!isWeb) return
    
    var showBanner by remember { mutableStateOf(true) }
    
    // Check localStorage for dismiss state
    LaunchedEffect(Unit) {
        val lastDismissTimeStr = AppStorage.get().loadString("android_promo_dismissed_at")
        val lastDismissTime = lastDismissTimeStr?.toLongOrNull() ?: 0L
        val currentTime = getCurrentTimeMs()
        val sevenDaysInMs = 7 * 24 * 60 * 60 * 1000
        
        if (lastDismissTime > 0 && (currentTime - lastDismissTime) < sevenDaysInMs) {
            showBanner = false
        }
    }
    
    // Creative taglines
    val taglines = listOf(
        "Extract text from images. Export notes as PDFs. Get the Android app →",
        "Scan flashcards. Create PDF studies. Download Android now →",
        "OCR scanning + PDF export. More power. Try Android →",
        "Study smarter: Image-to-text + PDF export. Get Android →",
        "Your notes in PDFs. Your images as text. Android app ready →"
    )
    
    // Rotate tagline randomly
    val selectedTagline = remember { taglines.random() }
    
    val handleDismiss = {
        showBanner = false
        AppStorage.get().saveString("android_promo_dismissed_at", getCurrentTimeMs().toString())
    }
    
    val handleDownload: () -> Unit = {
        // Open Google Play Store in new tab
        openPlayStore(
            "https://play.google.com/store/apps/details?id=com.karyadina.android&hl=en_IN"
        )
    }
    
    AnimatedVisibility(
        visible = showBanner,
        enter = fadeIn() + expandVertically(),
        exit = fadeOut() + shrinkVertically()
    ) {
        ElevatedCard(
            modifier = modifier.fillMaxWidth(),
            colors = CardDefaults.elevatedCardColors(
                containerColor = MaterialTheme.colorScheme.primaryContainer
            ),
            elevation = CardDefaults.elevatedCardElevation(defaultElevation = 4.dp)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    // Android Icon
                    Surface(
                        modifier = Modifier.size(48.dp),
                        shape = RoundedCornerShape(12.dp),
                        color = MaterialTheme.colorScheme.primary,
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            Icon(
                                imageVector = Icons.Filled.Android,
                                contentDescription = "Android App",
                                tint = MaterialTheme.colorScheme.onPrimary,
                                modifier = Modifier.size(28.dp)
                            )
                        }
                    }
                    
                    // Content
                    Column(
                        modifier = Modifier.weight(1f),
                        verticalArrangement = Arrangement.spacedBy(4.dp)
                    ) {
                        Text(
                            text = "Get more on Android",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onPrimaryContainer
                        )
                        Text(
                            text = selectedTagline,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onPrimaryContainer.copy(alpha = 0.8f),
                            lineHeight = 16.sp
                        )
                    }
                    
                    // Close Button
                    IconButton(
                        onClick = handleDismiss,
                        modifier = Modifier.size(40.dp)
                    ) {
                        Icon(
                            imageVector = Icons.Filled.Close,
                            contentDescription = "Dismiss",
                            tint = MaterialTheme.colorScheme.onPrimaryContainer.copy(alpha = 0.6f),
                            modifier = Modifier.size(20.dp)
                        )
                    }
                }
                
                // Download Button (Full width)
                Button(
                    onClick = handleDownload,
                    modifier = Modifier.fillMaxWidth(),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.primary
                    )
                ) {
                    Icon(
                        imageVector = Icons.Filled.Android,
                        contentDescription = null,
                        modifier = Modifier
                            .size(20.dp)
                            .padding(end = 8.dp)
                    )
                    Text("Download from Google Play")
                }
            }
        }
    }
}
